import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './ApproachingBuses.css';

// Component to display approaching buses with real-time updates
const ApproachingBuses = ({ stopNumber }) => {
  const { t } = useTranslation();
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchBuses = useCallback(async () => {
    if (!stopNumber) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`/api/BusStops/approaching-buses/${stopNumber}`);
      setBuses(response.data || []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching approaching buses:', err);
      setError(err.response?.data?.error || t('approachingBuses.error'));
      setBuses([]);
    } finally {
      setLoading(false);
    }
  }, [stopNumber, t]);

  useEffect(() => {
    fetchBuses();

    // Auto-refresh every 15 seconds
    const interval = setInterval(fetchBuses, 15000);

    return () => clearInterval(interval);
  }, [fetchBuses]);

  // Pause/resume when tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchBuses();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchBuses]);

  const getTimeColor = (minutesStr) => {
    const minutes = parseInt(minutesStr) || 0;
    if (minutes <= 3) return 'time-urgent';
    if (minutes <= 7) return 'time-soon';
    return 'time-later';
  };

  const formatTime = (minutesStr) => {
    const minutes = parseInt(minutesStr) || 0;
    if (minutes === 0) return t('approachingBuses.arriving');
    if (minutes === 1) return `1 ${t('approachingBuses.minute')}`;
    return `${minutes} ${t('approachingBuses.minutes')}`;
  };

  if (loading && buses.length === 0) {
    return (
      <div className="approaching-buses-loading">
        <div className="spinner"></div>
        <span>{t('approachingBuses.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="approaching-buses-error">
        <span className="error-icon">⚠️</span>
        <span>{error}</span>
      </div>
    );
  }

  if (buses.length === 0) {
    return (
      <div className="approaching-buses-empty">
        <span className="empty-icon">🚌</span>
        <span>{t('approachingBuses.noBuses')}</span>
      </div>
    );
  }

  return (
    <div className="approaching-buses">
      <div className="buses-header">
        <h3>{t('approachingBuses.title')}</h3>
        {lastUpdated && (
          <span className="last-updated">
            {t('approachingBuses.updated')}: {lastUpdated.toLocaleTimeString()}
          </span>
        )}
        {loading && <div className="inline-spinner"></div>}
      </div>

      <div className="buses-list">
        {buses.map((bus, index) => (
          <div key={index} className="bus-card">
            <div className="bus-route">
              <span className="bus-icon">🚌</span>
              <span className="route-number">{bus.routeNumber || '—'}</span>
            </div>
            <div className="bus-info">
              <div className="bus-destination">
                {bus.routeName || t('approachingBuses.unknown')}
              </div>
              <div className="bus-details">
                <span className="vehicle-id">🚍 {bus.vehicleId || '—'}</span>
                <span className="stops-away">📍 {bus.stopsAway || '—'} {t('approachingBuses.stops')}</span>
              </div>
              <div className={`bus-time ${getTimeColor(bus.timeInMinutes)}`}>
                ⏱ {formatTime(bus.timeInMinutes)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ApproachingBuses;
